/**
 * Constantes pour les pays - Focus Afrique
 * 
 * @description Liste des pays africains avec leurs codes ISO, drapeaux emoji et informations
 * @since 2025-12-29
 * 
 * Les drapeaux emoji sont générés à partir des codes ISO 3166-1 alpha-2
 * en utilisant les Regional Indicator Symbols (U+1F1E6 à U+1F1FF)
 */

export interface Country {
  code: string;      // Code ISO 3166-1 alpha-2
  code3: string;     // Code ISO 3166-1 alpha-3
  name: string;      // Nom en français
  nameEn: string;    // Nom en anglais
  flag: string;      // Emoji drapeau
  region: 'central' | 'east' | 'north' | 'south' | 'west';
  phoneCode: string; // Indicatif téléphonique
  currency: string;  // Code devise principale
}

/**
 * Génère un emoji drapeau à partir du code ISO alpha-2
 */
export const getFlagEmoji = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

/**
 * Pays africains - Liste complète (54 pays)
 * Organisés par région
 */
export const AFRICAN_COUNTRIES: Country[] = [
  // ============================================
  // AFRIQUE CENTRALE (9 pays)
  // ============================================
  {
    code: 'CD',
    code3: 'COD',
    name: 'République Démocratique du Congo',
    nameEn: 'Democratic Republic of the Congo',
    flag: '🇨🇩',
    region: 'central',
    phoneCode: '+243',
    currency: 'CDF',
  },
  {
    code: 'CG',
    code3: 'COG',
    name: 'République du Congo',
    nameEn: 'Republic of the Congo',
    flag: '🇨🇬',
    region: 'central',
    phoneCode: '+242',
    currency: 'XAF',
  },
  {
    code: 'CF',
    code3: 'CAF',
    name: 'République Centrafricaine',
    nameEn: 'Central African Republic',
    flag: '🇨🇫',
    region: 'central',
    phoneCode: '+236',
    currency: 'XAF',
  },
  {
    code: 'CM',
    code3: 'CMR',
    name: 'Cameroun',
    nameEn: 'Cameroon',
    flag: '🇨🇲',
    region: 'central',
    phoneCode: '+237',
    currency: 'XAF',
  },
  {
    code: 'GA',
    code3: 'GAB',
    name: 'Gabon',
    nameEn: 'Gabon',
    flag: '🇬🇦',
    region: 'central',
    phoneCode: '+241',
    currency: 'XAF',
  },
  {
    code: 'GQ',
    code3: 'GNQ',
    name: 'Guinée équatoriale',
    nameEn: 'Equatorial Guinea',
    flag: '🇬🇶',
    region: 'central',
    phoneCode: '+240',
    currency: 'XAF',
  },
  {
    code: 'TD',
    code3: 'TCD',
    name: 'Tchad',
    nameEn: 'Chad',
    flag: '🇹🇩',
    region: 'central',
    phoneCode: '+235',
    currency: 'XAF',
  },
  {
    code: 'ST',
    code3: 'STP',
    name: 'São Tomé-et-Príncipe',
    nameEn: 'São Tomé and Príncipe',
    flag: '🇸🇹',
    region: 'central',
    phoneCode: '+239',
    currency: 'STN',
  },
  {
    code: 'AO',
    code3: 'AGO',
    name: 'Angola',
    nameEn: 'Angola',
    flag: '🇦🇴',
    region: 'central',
    phoneCode: '+244',
    currency: 'AOA',
  },

  // ============================================
  // AFRIQUE DE L'EST (14 pays)
  // ============================================
  {
    code: 'RW',
    code3: 'RWA',
    name: 'Rwanda',
    nameEn: 'Rwanda',
    flag: '🇷🇼',
    region: 'east',
    phoneCode: '+250',
    currency: 'RWF',
  },
  {
    code: 'BI',
    code3: 'BDI',
    name: 'Burundi',
    nameEn: 'Burundi',
    flag: '🇧🇮',
    region: 'east',
    phoneCode: '+257',
    currency: 'BIF',
  },
  {
    code: 'UG',
    code3: 'UGA',
    name: 'Ouganda',
    nameEn: 'Uganda',
    flag: '🇺🇬',
    region: 'east',
    phoneCode: '+256',
    currency: 'UGX',
  },
  {
    code: 'TZ',
    code3: 'TZA',
    name: 'Tanzanie',
    nameEn: 'Tanzania',
    flag: '🇹🇿',
    region: 'east',
    phoneCode: '+255',
    currency: 'TZS',
  },
  {
    code: 'KE',
    code3: 'KEN',
    name: 'Kenya',
    nameEn: 'Kenya',
    flag: '🇰🇪',
    region: 'east',
    phoneCode: '+254',
    currency: 'KES',
  },
  {
    code: 'ET',
    code3: 'ETH',
    name: 'Éthiopie',
    nameEn: 'Ethiopia',
    flag: '🇪🇹',
    region: 'east',
    phoneCode: '+251',
    currency: 'ETB',
  },
  {
    code: 'ER',
    code3: 'ERI',
    name: 'Érythrée',
    nameEn: 'Eritrea',
    flag: '🇪🇷',
    region: 'east',
    phoneCode: '+291',
    currency: 'ERN',
  },
  {
    code: 'DJ',
    code3: 'DJI',
    name: 'Djibouti',
    nameEn: 'Djibouti',
    flag: '🇩🇯',
    region: 'east',
    phoneCode: '+253',
    currency: 'DJF',
  },
  {
    code: 'SO',
    code3: 'SOM',
    name: 'Somalie',
    nameEn: 'Somalia',
    flag: '🇸🇴',
    region: 'east',
    phoneCode: '+252',
    currency: 'SOS',
  },
  {
    code: 'SS',
    code3: 'SSD',
    name: 'Soudan du Sud',
    nameEn: 'South Sudan',
    flag: '🇸🇸',
    region: 'east',
    phoneCode: '+211',
    currency: 'SSP',
  },
  {
    code: 'SD',
    code3: 'SDN',
    name: 'Soudan',
    nameEn: 'Sudan',
    flag: '🇸🇩',
    region: 'east',
    phoneCode: '+249',
    currency: 'SDG',
  },
  {
    code: 'MG',
    code3: 'MDG',
    name: 'Madagascar',
    nameEn: 'Madagascar',
    flag: '🇲🇬',
    region: 'east',
    phoneCode: '+261',
    currency: 'MGA',
  },
  {
    code: 'MU',
    code3: 'MUS',
    name: 'Maurice',
    nameEn: 'Mauritius',
    flag: '🇲🇺',
    region: 'east',
    phoneCode: '+230',
    currency: 'MUR',
  },
  {
    code: 'SC',
    code3: 'SYC',
    name: 'Seychelles',
    nameEn: 'Seychelles',
    flag: '🇸🇨',
    region: 'east',
    phoneCode: '+248',
    currency: 'SCR',
  },
  {
    code: 'KM',
    code3: 'COM',
    name: 'Comores',
    nameEn: 'Comoros',
    flag: '🇰🇲',
    region: 'east',
    phoneCode: '+269',
    currency: 'KMF',
  },

  // ============================================
  // AFRIQUE DU NORD (7 pays)
  // ============================================
  {
    code: 'EG',
    code3: 'EGY',
    name: 'Égypte',
    nameEn: 'Egypt',
    flag: '🇪🇬',
    region: 'north',
    phoneCode: '+20',
    currency: 'EGP',
  },
  {
    code: 'LY',
    code3: 'LBY',
    name: 'Libye',
    nameEn: 'Libya',
    flag: '🇱🇾',
    region: 'north',
    phoneCode: '+218',
    currency: 'LYD',
  },
  {
    code: 'TN',
    code3: 'TUN',
    name: 'Tunisie',
    nameEn: 'Tunisia',
    flag: '🇹🇳',
    region: 'north',
    phoneCode: '+216',
    currency: 'TND',
  },
  {
    code: 'DZ',
    code3: 'DZA',
    name: 'Algérie',
    nameEn: 'Algeria',
    flag: '🇩🇿',
    region: 'north',
    phoneCode: '+213',
    currency: 'DZD',
  },
  {
    code: 'MA',
    code3: 'MAR',
    name: 'Maroc',
    nameEn: 'Morocco',
    flag: '🇲🇦',
    region: 'north',
    phoneCode: '+212',
    currency: 'MAD',
  },
  {
    code: 'MR',
    code3: 'MRT',
    name: 'Mauritanie',
    nameEn: 'Mauritania',
    flag: '🇲🇷',
    region: 'north',
    phoneCode: '+222',
    currency: 'MRU',
  },
  {
    code: 'EH',
    code3: 'ESH',
    name: 'Sahara occidental',
    nameEn: 'Western Sahara',
    flag: '🇪🇭',
    region: 'north',
    phoneCode: '+212',
    currency: 'MAD',
  },

  // ============================================
  // AFRIQUE AUSTRALE (10 pays)
  // ============================================
  {
    code: 'ZA',
    code3: 'ZAF',
    name: 'Afrique du Sud',
    nameEn: 'South Africa',
    flag: '🇿🇦',
    region: 'south',
    phoneCode: '+27',
    currency: 'ZAR',
  },
  {
    code: 'ZM',
    code3: 'ZMB',
    name: 'Zambie',
    nameEn: 'Zambia',
    flag: '🇿🇲',
    region: 'south',
    phoneCode: '+260',
    currency: 'ZMW',
  },
  {
    code: 'ZW',
    code3: 'ZWE',
    name: 'Zimbabwe',
    nameEn: 'Zimbabwe',
    flag: '🇿🇼',
    region: 'south',
    phoneCode: '+263',
    currency: 'ZWL',
  },
  {
    code: 'MW',
    code3: 'MWI',
    name: 'Malawi',
    nameEn: 'Malawi',
    flag: '🇲🇼',
    region: 'south',
    phoneCode: '+265',
    currency: 'MWK',
  },
  {
    code: 'MZ',
    code3: 'MOZ',
    name: 'Mozambique',
    nameEn: 'Mozambique',
    flag: '🇲🇿',
    region: 'south',
    phoneCode: '+258',
    currency: 'MZN',
  },
  {
    code: 'BW',
    code3: 'BWA',
    name: 'Botswana',
    nameEn: 'Botswana',
    flag: '🇧🇼',
    region: 'south',
    phoneCode: '+267',
    currency: 'BWP',
  },
  {
    code: 'NA',
    code3: 'NAM',
    name: 'Namibie',
    nameEn: 'Namibia',
    flag: '🇳🇦',
    region: 'south',
    phoneCode: '+264',
    currency: 'NAD',
  },
  {
    code: 'SZ',
    code3: 'SWZ',
    name: 'Eswatini',
    nameEn: 'Eswatini',
    flag: '🇸🇿',
    region: 'south',
    phoneCode: '+268',
    currency: 'SZL',
  },
  {
    code: 'LS',
    code3: 'LSO',
    name: 'Lesotho',
    nameEn: 'Lesotho',
    flag: '🇱🇸',
    region: 'south',
    phoneCode: '+266',
    currency: 'LSL',
  },
  {
    code: 'RE',
    code3: 'REU',
    name: 'Réunion',
    nameEn: 'Réunion',
    flag: '🇷🇪',
    region: 'south',
    phoneCode: '+262',
    currency: 'EUR',
  },

  // ============================================
  // AFRIQUE DE L'OUEST (16 pays)
  // ============================================
  {
    code: 'NG',
    code3: 'NGA',
    name: 'Nigéria',
    nameEn: 'Nigeria',
    flag: '🇳🇬',
    region: 'west',
    phoneCode: '+234',
    currency: 'NGN',
  },
  {
    code: 'GH',
    code3: 'GHA',
    name: 'Ghana',
    nameEn: 'Ghana',
    flag: '🇬🇭',
    region: 'west',
    phoneCode: '+233',
    currency: 'GHS',
  },
  {
    code: 'CI',
    code3: 'CIV',
    name: "Côte d'Ivoire",
    nameEn: 'Ivory Coast',
    flag: '🇨🇮',
    region: 'west',
    phoneCode: '+225',
    currency: 'XOF',
  },
  {
    code: 'SN',
    code3: 'SEN',
    name: 'Sénégal',
    nameEn: 'Senegal',
    flag: '🇸🇳',
    region: 'west',
    phoneCode: '+221',
    currency: 'XOF',
  },
  {
    code: 'ML',
    code3: 'MLI',
    name: 'Mali',
    nameEn: 'Mali',
    flag: '🇲🇱',
    region: 'west',
    phoneCode: '+223',
    currency: 'XOF',
  },
  {
    code: 'BF',
    code3: 'BFA',
    name: 'Burkina Faso',
    nameEn: 'Burkina Faso',
    flag: '🇧🇫',
    region: 'west',
    phoneCode: '+226',
    currency: 'XOF',
  },
  {
    code: 'NE',
    code3: 'NER',
    name: 'Niger',
    nameEn: 'Niger',
    flag: '🇳🇪',
    region: 'west',
    phoneCode: '+227',
    currency: 'XOF',
  },
  {
    code: 'BJ',
    code3: 'BEN',
    name: 'Bénin',
    nameEn: 'Benin',
    flag: '🇧🇯',
    region: 'west',
    phoneCode: '+229',
    currency: 'XOF',
  },
  {
    code: 'TG',
    code3: 'TGO',
    name: 'Togo',
    nameEn: 'Togo',
    flag: '🇹🇬',
    region: 'west',
    phoneCode: '+228',
    currency: 'XOF',
  },
  {
    code: 'GN',
    code3: 'GIN',
    name: 'Guinée',
    nameEn: 'Guinea',
    flag: '🇬🇳',
    region: 'west',
    phoneCode: '+224',
    currency: 'GNF',
  },
  {
    code: 'GW',
    code3: 'GNB',
    name: 'Guinée-Bissau',
    nameEn: 'Guinea-Bissau',
    flag: '🇬🇼',
    region: 'west',
    phoneCode: '+245',
    currency: 'XOF',
  },
  {
    code: 'SL',
    code3: 'SLE',
    name: 'Sierra Leone',
    nameEn: 'Sierra Leone',
    flag: '🇸🇱',
    region: 'west',
    phoneCode: '+232',
    currency: 'SLL',
  },
  {
    code: 'LR',
    code3: 'LBR',
    name: 'Libéria',
    nameEn: 'Liberia',
    flag: '🇱🇷',
    region: 'west',
    phoneCode: '+231',
    currency: 'LRD',
  },
  {
    code: 'CV',
    code3: 'CPV',
    name: 'Cap-Vert',
    nameEn: 'Cape Verde',
    flag: '🇨🇻',
    region: 'west',
    phoneCode: '+238',
    currency: 'CVE',
  },
  {
    code: 'GM',
    code3: 'GMB',
    name: 'Gambie',
    nameEn: 'Gambia',
    flag: '🇬🇲',
    region: 'west',
    phoneCode: '+220',
    currency: 'GMD',
  },
  {
    code: 'SH',
    code3: 'SHN',
    name: 'Sainte-Hélène',
    nameEn: 'Saint Helena',
    flag: '🇸🇭',
    region: 'west',
    phoneCode: '+290',
    currency: 'SHP',
  },
];

/**
 * Pays prioritaires pour la RDC (pays voisins et partenaires commerciaux)
 */
export const PRIORITY_COUNTRIES_DRC: string[] = [
  'CD', // RDC - toujours en premier
  'CG', // Congo-Brazzaville
  'RW', // Rwanda
  'BI', // Burundi
  'UG', // Ouganda
  'TZ', // Tanzanie
  'ZM', // Zambie
  'AO', // Angola
  'CF', // Centrafrique
  'SS', // Soudan du Sud
];

/**
 * Génère les options pour un select avec les pays africains
 * Les pays prioritaires sont affichés en premier, suivis d'un séparateur
 */
export const getAfricanCountryOptions = (priorityFirst = true): { value: string; label: string }[] => {
  if (!priorityFirst) {
    return AFRICAN_COUNTRIES.map(country => ({
      value: country.code,
      label: `${country.flag} ${country.name}`,
    }));
  }

  // Pays prioritaires en premier
  const priorityCountries = PRIORITY_COUNTRIES_DRC
    .map(code => AFRICAN_COUNTRIES.find(c => c.code === code))
    .filter((c): c is Country => c !== undefined);

  // Autres pays (non prioritaires)
  const otherCountries = AFRICAN_COUNTRIES
    .filter(c => !PRIORITY_COUNTRIES_DRC.includes(c.code))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));

  return [
    // Pays prioritaires
    ...priorityCountries.map(country => ({
      value: country.code,
      label: `${country.flag} ${country.name}`,
    })),
    // Séparateur visuel
    { value: '', label: '──────────────' },
    // Autres pays
    ...otherCountries.map(country => ({
      value: country.code,
      label: `${country.flag} ${country.name}`,
    })),
  ];
};

/**
 * Obtenir les options de pays par région
 */
export const getCountriesByRegion = (region: Country['region']): { value: string; label: string }[] => {
  return AFRICAN_COUNTRIES
    .filter(c => c.region === region)
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
    .map(country => ({
      value: country.code,
      label: `${country.flag} ${country.name}`,
    }));
};

/**
 * Trouver un pays par son code
 */
export const getCountryByCode = (code: string): Country | undefined => {
  return AFRICAN_COUNTRIES.find(c => c.code === code || c.code3 === code);
};

/**
 * Obtenir le nom d'un pays à partir de son code
 */
export const getCountryName = (code: string): string => {
  const country = getCountryByCode(code);
  return country ? country.name : code;
};

/**
 * Obtenir le drapeau d'un pays à partir de son code
 */
export const getCountryFlag = (code: string): string => {
  const country = getCountryByCode(code);
  return country ? country.flag : getFlagEmoji(code);
};

/**
 * Régions africaines
 */
export const AFRICAN_REGIONS = [
  { value: 'central', label: '🌍 Afrique Centrale' },
  { value: 'east', label: '🌍 Afrique de l\'Est' },
  { value: 'north', label: '🌍 Afrique du Nord' },
  { value: 'south', label: '🌍 Afrique Australe' },
  { value: 'west', label: '🌍 Afrique de l\'Ouest' },
];

// Export par défaut de la liste des pays africains formatée pour les selects
export default getAfricanCountryOptions;
